/**
 * Nexuma — Service Worker Registration
 *
 * Strategjia e përditësimeve për PWA të instaluar:
 *   1. Regjistrimi i fillimit (load)
 *   2. Polling periodik:    çdo 15 min thirret `registration.update()`
 *   3. Visibility change:   kur PWA kthehet në fokus → check për update
 *   4. Online event:        kur lidhja kthehet → check për update
 *   5. Navigation:          në çdo route change të aplikacionit (opsional)
 *
 * Kjo siguron që PWA e instaluar (manifest) i merr përditësimet pa rifreskim
 * manual nga përdoruesi. Banner-i shfaqet vetëm kur ka version të ri të
 * gatshëm; përdoruesi mund ta aktivizojë me 1 klik ose pranoi auto-reload.
 */

const SW_URL = '/sw.js';
const UPDATE_CHECK_INTERVAL_MS = 15 * 60 * 1000;   // 15 min
const AUTO_RELOAD_AFTER_INSTALL_MS = 0;            // 0 = vetëm banner; >0 = auto-reload pas X ms

let _registration = null;
let _pollTimer    = null;

export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;

  // Në localhost: çregjistro çdo SW ekzistues dhe dil
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    navigator.serviceWorker.getRegistrations().then(regs => {
      regs.forEach(r => r.unregister());
    });
    return;
  }

  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register(SW_URL, {
        scope: '/',
        updateViaCache: 'none',
      });
      _registration = registration;
      console.log('[SW] Regjistruar:', registration.scope);

      attachUpdateListeners(registration);
      startUpdatePolling(registration);
      attachVisibilityHooks(registration);
      attachOnlineHook(registration);

      // Një SW në pritje (faqja u hap pas një deploy-i) e kap `swUpdate.js`,
      // i cili e lexon vetë `registration.waiting` dhe njofton PWAOverlay-n.
    } catch (err) {
      console.error('[SW] Regjistrimi dështoi:', err);
    }
  });

  // Dëgjo mesazhe nga SW (p.sh. backend i kërkon reload)
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'RELOAD_PAGE') {
      window.location.reload();
    }
  });

  // Kur SW i ri merr kontrollin, rifresko sapo të jetë i sigurt
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });
}

/* ──────────────────────────────────────────────────────────────────────────
 * Update detection
 * ────────────────────────────────────────────────────────────────────────── */
function attachUpdateListeners(registration) {
  registration.addEventListener('updatefound', () => {
    const newWorker = registration.installing;
    if (!newWorker) return;

    console.log('[SW] Po instalohet versioni i ri…');
    newWorker.addEventListener('statechange', () => {
      if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
        // Ka version të ri që pret aktivizim. Kur `AUTO_RELOAD_AFTER_INSTALL_MS`
        // është 0, nuk bëhet asgjë këtu: vendimin e merr përdoruesi te njoftimi
        // i PWAOverlay-t, ndaj faqja nuk rifreskohet nën duart e tij.
        if (AUTO_RELOAD_AFTER_INSTALL_MS > 0) {
          setTimeout(() => activateUpdate(registration), AUTO_RELOAD_AFTER_INSTALL_MS);
        }
      }
    });
  });
}

/**
 * Polling periodik: çdo X min thirret update() për të detektuar versionet e reja.
 */
function startUpdatePolling(registration) {
  if (_pollTimer) clearInterval(_pollTimer);
  _pollTimer = setInterval(() => {
    triggerUpdate(registration, 'periodic');
  }, UPDATE_CHECK_INTERVAL_MS);
}

/**
 * Kur PWA kthehet në fokus, kontrollo menjëherë për update.
 */
function attachVisibilityHooks(registration) {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      triggerUpdate(registration, 'visibilitychange');
    }
  });
  window.addEventListener('focus', () => {
    triggerUpdate(registration, 'focus');
  });
  window.addEventListener('pageshow', (e) => {
    // pageshow firohet edhe kur PWA hapet nga shortcut/bfcache
    if (e.persisted) triggerUpdate(registration, 'pageshow');
  });
}

/**
 * Kur lidhja kthehet online, kontrollo për update.
 */
function attachOnlineHook(registration) {
  window.addEventListener('online', () => {
    triggerUpdate(registration, 'online');
  });
}

let _lastCheckAt = 0;
const MIN_CHECK_INTERVAL_MS = 30_000;   // mos thirr update() më shpesh se çdo 30s

function triggerUpdate(registration, reason) {
  const now = Date.now();
  if (now - _lastCheckAt < MIN_CHECK_INTERVAL_MS) return;
  _lastCheckAt = now;
  console.log('[SW] Po kontrollon për update (' + reason + ')…');
  registration.update().catch(err => {
    console.warn('[SW] update() dështoi:', err);
  });
}

/**
 * Aktivizon SW-në në pritje dhe rifreskon faqen.
 */
function activateUpdate(registration) {
  const worker = registration.waiting || registration.installing;
  if (worker) worker.postMessage({ type: 'SKIP_WAITING' });
  // `controllerchange` listener-i do të bëjë reload automatikisht
}

/**
 * Funksion publik për të kontrolluar manualisht për update (p.sh. nga UI).
 */
export function checkForUpdate() {
  if (!_registration) return Promise.resolve(false);
  return _registration.update().then(() => true).catch(() => false);
}

/**
 * Force-aktivizo update-in e gatshëm (përdorur nga UI ose nga banner).
 */
export function applyUpdate() {
  if (!_registration) return;
  activateUpdate(_registration);
}

export function unregisterServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  if (_pollTimer) { clearInterval(_pollTimer); _pollTimer = null; }
  navigator.serviceWorker.ready
    .then((reg) => reg.unregister())
    .catch((err) => console.error('[SW] Unregister dështoi:', err));
}

/* ──────────────────────────────────────────────────────────────────────────
 * Njoftimi i versionit të ri NUK jeton këtu.
 *
 * Ky modul dikur injektonte një kartë DOM (`#sw-update-banner`) me të njëjtin
 * mesazh që shfaq edhe `components/pwa/PWAOverlay.jsx`, i cili dëgjon të njëjtën
 * ngjarje përmes `services/pwa/swUpdate.js`. Rezultati: dy njoftime njëherësh
 * për një përditësim të vetëm, në dy stile të ndryshme dhe në dy cepa të ekranit.
 *
 * Regjistrimi i service worker-it mbetet punë e këtij moduli; shfaqja e tij i
 * takon React-it, ku ndjek dizajnin e app-it dhe mund të mbyllet e testohet.
 * ────────────────────────────────────────────────────────────────────────── */

/* ──────────────────────────────────────────────────────────────────────────
 * Connectivity helpers
 * ────────────────────────────────────────────────────────────────────────── */
export function watchConnectivity(onOnline, onOffline) {
  window.addEventListener('online',  () => onOnline?.());
  window.addEventListener('offline', () => onOffline?.());
  return navigator.onLine;
}

export async function requestPushPermission() {
  if (!('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  const result = await Notification.requestPermission();
  return result;
}
